import React from 'react';
import { FilmSlate, FileText, MagnifyingGlass } from '@phosphor-icons/react';
import { Flex, UnderlineTab, UnderlineTabs } from '@chatsdet/ui';
import type { VideoTimeline, VideoTimelineEntry } from '@chatsdet/types';
import {
  SpecReviewCleanedSpec,
  SpecReviewResearch,
} from '../../../features/exploratory-tests/components/SpecReviewMainSections';
import type { ExploratoryTestEnvironmentInstance } from '../../../features/exploratory-tests';
import type { SpecReviewReportJson } from '../../../features/exploratory-tests/types';
import type { useBrowserStream } from '../../../hooks/useBrowserStream';
import type { ExploratoryBrowserSession } from '../../../features/exploratory-tests/types';
import { ExploratoryDetailMedia } from './ExploratoryDetailMedia';

type SpecReviewMainTab = 'video' | 'cleanedSpec' | 'research';

type SpecReviewMainTabsProps = {
  report: SpecReviewReportJson | null;
  isPlanning: boolean;
  isRunning: boolean;
  planningStartedAt: string | null;
  environmentInstance: ExploratoryTestEnvironmentInstance | null;
  browserState: ReturnType<typeof useBrowserStream>['state'];
  videoSrc: string | null;
  timeline: VideoTimeline | null;
  onAnnotationClick: (entry: VideoTimelineEntry) => void;
  browserSessions: ExploratoryBrowserSession[];
  runId?: string;
  selectedBrowserSessionId?: string | null;
  onBrowserSessionSelect?: (id: string) => void;
};

/**
 * Main-column tabs for the spec-review flavor: the run video, the AI-cleaned
 * spec, and the research ledger (assumptions/idempotency/data dependencies) —
 * all get full-width top-level tabs instead of competing for space in the
 * 380px sidebar, which now only holds the compact overview plus per-item
 * detail and findings.
 */
export function SpecReviewMainTabs({
  report,
  isPlanning,
  isRunning,
  planningStartedAt,
  environmentInstance,
  browserState,
  videoSrc,
  timeline,
  onAnnotationClick,
  browserSessions,
  runId,
  selectedBrowserSessionId,
  onBrowserSessionSelect,
}: SpecReviewMainTabsProps) {
  const [activeTab, setActiveTab] = React.useState<SpecReviewMainTab>('video');

  // A session picked from outside this component (e.g. a finding's timestamp
  // in the sidebar) must be visible, so bring the Video tab forward.
  React.useEffect(() => {
    if (selectedBrowserSessionId) setActiveTab('video');
  }, [selectedBrowserSessionId]);

  return (
    <div className="flex flex-1 min-h-0 flex-col overflow-hidden">
      <UnderlineTabs
        selectedId={activeTab}
        onSelectionChange={(id) => setActiveTab(id as SpecReviewMainTab)}
        className="bg-background px-4"
      >
        <UnderlineTab id="video" className="py-2.5">
          <Flex align="center" gap="xs">
            <FilmSlate size={16} weight="bold" />
            Video
          </Flex>
        </UnderlineTab>
        <UnderlineTab id="cleanedSpec" className="py-2.5">
          <Flex align="center" gap="xs">
            <FileText size={16} weight="bold" />
            Cleaned spec
          </Flex>
        </UnderlineTab>
        <UnderlineTab id="research" className="py-2.5">
          <Flex align="center" gap="xs">
            <MagnifyingGlass size={16} weight="bold" />
            Research
          </Flex>
        </UnderlineTab>
      </UnderlineTabs>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {activeTab === 'video' && (
          <div className="h-full bg-background">
            <ExploratoryDetailMedia
              isPlanning={isPlanning}
              isRunning={isRunning}
              planningStartedAt={planningStartedAt}
              environmentInstance={environmentInstance}
              browserState={browserState}
              videoSrc={videoSrc}
              timeline={timeline}
              onAnnotationClick={onAnnotationClick}
              browserSessions={browserSessions}
              runId={runId}
              selectedBrowserSessionId={selectedBrowserSessionId}
              onBrowserSessionSelect={onBrowserSessionSelect}
              flush
            />
          </div>
        )}
        {activeTab === 'cleanedSpec' && (
          <div className="min-h-full bg-card px-8 py-6">
            <SpecReviewCleanedSpec cleanedSpec={report?.cleanedSpec ?? null} />
          </div>
        )}
        {activeTab === 'research' && (
          <div className="min-h-full bg-card px-6 py-4">
            {report ? (
              <SpecReviewResearch report={report} />
            ) : (
              <Flex align="center" justify="center" className="p-8 text-muted-foreground">
                No report yet.
              </Flex>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
